import { ComponentType } from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Action, Actions, ofActionSuccessful, State, StateContext } from '@ngxs/store';
import { CloseAllSlidePanels, OpenSlidePanel, SlidePanelClosed } from './actions/slide-panel.actions';
import { SlidePanelCloseTypesEnum, SlidePanelConfig, SlidePanelPositioningTypesEnum, SlidePanelService } from '@bioreferencehealth/ui-kit';
import { finalize, Observable, race, tap } from 'rxjs';

export interface SlidePanelStateModel {
  component?: ComponentType<unknown>;
  config?: SlidePanelConfig;
}

@UntilDestroy()
@State<SlidePanelStateModel>({
  name: 'slidePanel'
})
@Injectable()
export class SlidePanelState {
  private readonly slidePanelService: SlidePanelService = inject(SlidePanelService);
  private readonly actions$: Actions = inject(Actions);

  @Action(OpenSlidePanel)
  public openSlidePanel(ctx: StateContext<SlidePanelStateModel>, action: OpenSlidePanel<unknown>): Observable<unknown> {
    if (action.config && !action.config.offsetTop && action.config.slidePanelPositioningType === SlidePanelPositioningTypesEnum.FromRight) {
      const offsetTop = document.querySelector<HTMLElement>('header')?.offsetHeight ?? 0;
      const heightOffset = offsetTop - Math.min(document.body.parentElement?.scrollTop ?? 0, offsetTop);
      action.config.offsetTop = `${heightOffset}px`;
    }

    const slidePanelRef = this.slidePanelService.open(action.component, action.config);

    const closeAll$ = this.actions$.pipe(
      ofActionSuccessful(CloseAllSlidePanels),
      tap(() => slidePanelRef.close(SlidePanelCloseTypesEnum.Cancel))
    );

    return race(slidePanelRef.afterClosed(), closeAll$).pipe(
      untilDestroyed(this),
      finalize(() => ctx.dispatch(new SlidePanelClosed(action.component)))
    );
  }

  @Action(CloseAllSlidePanels)
  public closeAllSlidePanels(ctx: StateContext<SlidePanelStateModel>): void {
    ctx.setState({ component: undefined, config: undefined });
  }
}
